import { useEffect, useRef, useState, type ReactNode } from "react";
import { IconAlert, IconCheck, IconInbox } from "./icons";

// Shared primitives. Everything visual in the app is assembled from these, so
// a spacing or colour decision is made once here rather than re-typed as an
// inline style on every page — which is how the previous build ended up with
// four different greys for the same idea.

/* ------------------------------------------------------------------ text */

export function PageTitle({ children }: { children: ReactNode }) {
  return <h2 className="t-title" style={{ marginBottom: 6 }}>{children}</h2>;
}

export function Label({ children }: { children: ReactNode }) {
  return <div className="t-label">{children}</div>;
}

/* ---------------------------------------------------------------- number */

/**
 * Money and quantities. Locale-formatted with a thousands separator, set in
 * tabular figures, and never a bare float — an unseparated 25094.91 is both
 * harder to read and easy to misread by an order of magnitude.
 */
export function Num({
  value,
  dp = 0,
  prefix,
  suffix,
  tone,
}: {
  value: number | string | null | undefined;
  dp?: number;
  prefix?: string;
  suffix?: string;
  tone?: "gain" | "loss" | "warn";
}) {
  const n = typeof value === "string" ? Number(value) : value;
  if (n === null || n === undefined || !Number.isFinite(n)) {
    return <span className="dimmer">—</span>;
  }
  const text = n.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
  return (
    <span className={`num${tone ? ` ${tone}` : ""}`}>
      {prefix}
      {text}
      {suffix && <span className="stat-unit">{suffix}</span>}
    </span>
  );
}

/**
 * Decimals an asset is actually quoted in. A EUR balance shown as
 * 3,100,000.00000000 is not more precise, only harder to read — and it makes
 * the fiat and crypto columns look like they carry the same kind of number
 * when they do not.
 */
const ASSET_DP: Record<string, number> = {
  EUR: 2, USD: 2, CHF: 2, CAD: 2, GBP: 2,
  BTC: 8, ETH: 8, SOL: 6, XRP: 6, ADA: 6, LTC: 8, AVAX: 8, POL: 8, TRX: 6,
  USDT_ERC20: 2, USDT_TRC20: 2, USDC: 2, USDC_POLYGON: 2, DAI: 2,
};

export function assetDp(asset: string | null | undefined): number {
  if (!asset) return 8;
  return ASSET_DP[asset.toUpperCase()] ?? 8;
}

/**
 * Balance-sheet precision: two decimals, because a venue inventory is read for
 * its magnitude and 26.20000000 BTC is six zeros of noise around the only two
 * digits anyone is looking at.
 *
 * The one exception is a holding too small to survive the rounding. Showing
 * "0.00" for a real, non-zero balance would say the desk holds nothing at that
 * venue, which is a different and much worse error than showing extra digits —
 * so anything that would round away keeps enough precision to stay visible.
 */
export function balanceDp(asset: string | null | undefined, amount: string | number): number {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return 2;
  if (Math.abs(n) >= 0.005) return 2;
  return assetDp(asset);
}

/** Abbreviates only above a thousand — "412" reads worse as "0.4k". */
export function compact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return n.toFixed(0);
}

/* ------------------------------------------------------------------ card */

export function Card({
  children,
  className = "",
  pad = true,
}: {
  children: ReactNode;
  className?: string;
  pad?: boolean;
}) {
  return <div className={`card${pad ? " card-pad" : ""} ${className}`}>{children}</div>;
}

export function CardHead({
  title,
  right,
  plain,
}: {
  title: ReactNode;
  right?: ReactNode;
  plain?: boolean;
}) {
  // `plain` renders the title as a heading rather than the uppercase micro
  // label. Proper nouns must use it: uppercasing turns the venue module's
  // carefully-cased "FalconX" and "B2C2" back into the flat "FALCONX" it
  // exists to prevent.
  return (
    <div className="card-head">
      {plain ? <div className="t-heading" style={{ marginRight: "auto" }}>{title}</div> : <Label>{title}</Label>}
      {right}
    </div>
  );
}

/* ------------------------------------------------------------------ stat */

export function Stat({
  label,
  value,
  unit,
  sub,
  tone,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  sub?: ReactNode;
  tone?: "gain" | "loss" | "warn";
}) {
  return (
    <div className="stat enter">
      <div className="stat-label">{label}</div>
      <div className={`stat-value${tone ? ` ${tone}` : ""}`}>
        {value}
        {unit && <span className="stat-unit">{unit}</span>}
      </div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ pill */

export type Tone = "neutral" | "info" | "good" | "bad" | "warn";

export function Pill({ tone = "neutral", dot, children }: { tone?: Tone; dot?: boolean; children: ReactNode }) {
  return (
    <span className={`pill ${tone}`}>
      {dot && <span className="pill-dot" />}
      {children}
    </span>
  );
}

/* ---------------------------------------------------------------- banner */

export function Banner({ tone, children }: { tone: "error" | "ok" | "warn" | "info"; children: ReactNode }) {
  return (
    <div className={`banner ${tone}`} role={tone === "error" ? "alert" : "status"}>
      {tone === "ok" ? <IconCheck /> : <IconAlert />}
      <div>{children}</div>
    </div>
  );
}

/* --------------------------------------------------------------- loading */

export function Skeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="card card-pad" aria-busy="true" aria-live="polite">
      <span className="skel skel-line" style={{ display: "block", width: "28%", marginBottom: 18 }} />
      {Array.from({ length: rows }).map((_, i) => (
        <span
          key={i}
          className="skel skel-line"
          style={{ display: "block", width: `${94 - (i % 3) * 16}%` }}
        />
      ))}
    </div>
  );
}

export function StatSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-stats">
      {Array.from({ length: count }).map((_, i) => (
        <div className="stat" key={i}>
          <span className="skel skel-line" style={{ display: "block", width: "52%", height: 9 }} />
          <span className="skel skel-line" style={{ display: "block", width: "72%", height: 22, marginTop: 12 }} />
        </div>
      ))}
    </div>
  );
}

export function Empty({ title, body, icon }: { title: string; body?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-icon">{icon ?? <IconInbox />}</div>
      <div className="empty-title">{title}</div>
      {body && <div className="empty-body">{body}</div>}
    </div>
  );
}

/* ------------------------------------------------------------- freshness */

/**
 * How old the data on screen is.
 *
 * Polled views silently present a stale number as current the moment a fetch
 * starts failing, and on a desk that is the difference between a decision and
 * a mistake. Past two poll intervals this stops claiming to be live and says
 * how old it actually is.
 */
export function Freshness({ at, intervalMs = 30_000 }: { at: number | null; intervalMs?: number }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (!at) return null;
  const age = Date.now() - at;
  const stale = age > intervalMs * 2;
  const secs = Math.round(age / 1000);
  const text = stale
    ? secs >= 120
      ? `stale, ${Math.round(secs / 60)} min old`
      : `stale, ${secs}s old`
    : "Live";

  return (
    <span className={`status ${stale ? "stale" : "live"}`} title={new Date(at).toLocaleString()}>
      <span className="status-dot" />
      {text}
    </span>
  );
}

/* ----------------------------------------------------------------- modal */

/**
 * Replaces window.prompt/alert. Beyond looking like the rest of the app, a
 * real dialog can be labelled for screen readers, can validate before it
 * closes, and can put the destructive action behind a distinct button — none
 * of which a native prompt can do.
 */
export function Modal({
  title,
  body,
  confirmLabel = "Confirm",
  tone = "primary",
  requireNote,
  notePlaceholder,
  onConfirm,
  onCancel,
}: {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  tone?: "primary" | "danger";
  requireNote?: boolean;
  notePlaceholder?: string;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const okRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    (requireNote ? inputRef.current : okRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, requireNote]);

  const blocked = Boolean(requireNote) && note.trim().length === 0;

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <div className="t-heading">{title}</div>
        </div>
        <div className="modal-body">
          {body}
          {(requireNote || notePlaceholder) && (
            <textarea
              ref={inputRef}
              rows={3}
              value={note}
              placeholder={notePlaceholder}
              onChange={(e) => setNote(e.target.value)}
              style={{ marginTop: 12 }}
            />
          )}
        </div>
        <div className="modal-foot">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            ref={okRef}
            type="button"
            className={`btn ${tone === "danger" ? "btn-danger" : "btn-primary"}`}
            disabled={blocked}
            onClick={() => onConfirm(note.trim())}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- toast */

export interface Toast {
  id: number;
  tone: "ok" | "error";
  text: string;
}

export function Toasts({ items, onDone }: { items: Toast[]; onDone: (id: number) => void }) {
  useEffect(() => {
    const timers = items.map((t) => setTimeout(() => onDone(t.id), 5000));
    return () => timers.forEach(clearTimeout);
  }, [items, onDone]);

  if (items.length === 0) return null;
  return (
    <div className="toasts" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`toast ${t.tone}`}>
          {t.tone === "ok" ? <IconCheck /> : <IconAlert />}
          <div>{t.text}</div>
        </div>
      ))}
    </div>
  );
}

/** Minimal toast queue — one hook so pages don't each invent their own. */
export function useToasts() {
  const [items, setItems] = useState<Toast[]>([]);
  const next = useRef(1);
  return {
    items,
    push: (tone: Toast["tone"], text: string) =>
      setItems((all) => [...all, { id: next.current++, tone, text }]),
    dismiss: (id: number) => setItems((all) => all.filter((t) => t.id !== id)),
  };
}
